import { useQuery } from "@tanstack/react-query";

import { uploadManager, type UploadItem } from "./uploadManager";

// AssetMetadata is collected in the upload modal but is NOT yet sent to the
// backend on upload — `ReserveAsset` does not accept these fields. They are
// reserved here for a future enhancement that applies metadata via
// `UpdateAsset` after the asset becomes available, or via an extension to
// `ReserveAssetRequest`. The fields render disabled in the metadata step
// until that work lands.
export type AssetMetadata = {
  tags: string[];
  brandId: string | null;
  categoryId: string | null;
  subcategoryId: string | null;
};

export const defaultAssetMetadata = (): AssetMetadata => ({
  tags: [],
  brandId: null,
  categoryId: null,
  subcategoryId: null,
});

/**
 * Reactive view onto the upload manager's state.
 *
 * The query at `['uploads']` is a "client-only query": its `queryFn` returns a
 * synchronous snapshot from the singleton manager, and `staleTime: Infinity`
 * keeps React Query from refetching. The manager drives all updates by calling
 * `queryClient.setQueryData(['uploads'], next)`, which notifies subscribers.
 *
 * Do not add a network-bound `queryFn` here — there is no server-side
 * representation of upload state. See `uploadManager.ts` for the source of
 * truth.
 */
export const useUploads = () => {
  const items =
    useQuery({
      queryKey: ["uploads"],
      queryFn: () => uploadManager.getItems(),
      initialData: [] as UploadItem[],
      staleTime: Infinity,
    }).data ?? [];

  return {
    items,
    addFiles: uploadManager.addFiles.bind(uploadManager),
    cancel: uploadManager.cancel.bind(uploadManager),
    hasActiveUploads: () =>
      items.some((i) => i.status === "queued" || i.status === "uploading"),
  };
};
